import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import "./ExitIntentPopup.scss";
import ModalCloseButton from "components/ModalCloseButton";
import ConfigurablePopup from "components/ConfigurablePopup";
import { POPUP_TYPES, canShowPopup, loadPopup, markPopupShown } from "utils/popups";

// The offer this popup advertises. Currency-neutral by default so it can never
// show euros on a USD brand — pass `offerText` to name a concrete amount.
const DEFAULT_OFFER_TEXT = "welcome offer";

const ExitIntentPopup = ({ isAuthenticated, setModalSignUpPropsData, offerText = DEFAULT_OFFER_TEXT }) => {
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownOnce, setHasShownOnce] = useState(false);
  const [isActive, setIsActive] = useState(false);
  // The admin-configured exit_intent pop-up, or null. Until /initial-settings
  // has answered this stays undefined, which is treated as "not configured" —
  // detection is unchanged either way, only the rendered content differs.
  const [configPopup, setConfigPopup] = useState(null);

  useEffect(() => {
    if (isAuthenticated) return;
    let alive = true;
    loadPopup(POPUP_TYPES.EXIT_INTENT).then((popup) => {
      if (alive) setConfigPopup(popup);
    });
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) return;

    // Frequency is admin-configured once a pop-up is set up; without one the
    // historical once-per-session flag stays in charge.
    const alreadyCapped = configPopup
      ? !canShowPopup(configPopup)
      : sessionStorage.getItem("exitPopupShown") === "true";
    if (alreadyCapped) {
      setHasShownOnce(true);
      return;
    }

    const recordShown = () => {
      setShowExitPopup(true);
      setHasShownOnce(true);
      if (configPopup) markPopupShown(configPopup);
      else sessionStorage.setItem("exitPopupShown", "true");
    };

    // Detect actual exit attempts (browser close, tab close, navigation away)
    const handleBeforeUnload = (e) => {
      if (!hasShownOnce && isActive) {
        // Show the popup
        recordShown();

        // Prevent default browser dialog (optional)
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';

        // Return false to show popup
        return false;
      }
    };

    // Detect mouse leaving to close tab/browser (backup method)
    const handleMouseOut = (e) => {
      // Only trigger if:
      // 1. Mouse is leaving from top (towards browser controls)
      // 2. Mouse is moving upward (toElement is null or outside document)
      // 3. Not already shown
      // 4. Exit detection is active
      if (
        !hasShownOnce &&
        !showExitPopup &&
        isActive &&
        e.clientY <= 5 &&
        (!e.toElement && !e.relatedTarget)
      ) {
        recordShown();
      }
    };

    // Activate exit intent after delay
    const activationTimer = setTimeout(() => {
      setIsActive(true);
    }, 3000); // Wait 3 seconds before activation

    // Add event listeners when active
    if (isActive) {
      // Detect actual close/exit attempts
      window.addEventListener("beforeunload", handleBeforeUnload);
      // Backup: detect mouse leaving to close
      document.documentElement.addEventListener("mouseout", handleMouseOut);
      document.documentElement.addEventListener("mouseleave", handleMouseOut);
    }

    return () => {
      clearTimeout(activationTimer);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.documentElement.removeEventListener("mouseout", handleMouseOut);
      document.documentElement.removeEventListener("mouseleave", handleMouseOut);
    };
  }, [hasShownOnce, showExitPopup, isActive, isAuthenticated, configPopup]);

  const handleClose = () => {
    setShowExitPopup(false);
  };

  // The consumer tests `props.modalSignUpPropsData == true` (LoginReg), so the
  // sign-up modal only opens on a boolean pulse — the same one Header sends.
  const openSignUpModal = () => {
    if (!setModalSignUpPropsData) return;
    setModalSignUpPropsData(true);
    setTimeout(() => setModalSignUpPropsData(false), 500);
  };

  const handleClaim = () => {
    handleClose();
    openSignUpModal();
  };

  // Nothing here applies to a signed-in player, and the once-per-session flag
  // must not be burned on them either.
  if (isAuthenticated) return null;

  // An enabled exit_intent pop-up replaces the hardcoded copy entirely. The
  // detection above is untouched. Keeping the CTA on the sign-up modal (rather
  // than routing to /register) preserves exactly what the live button does.
  if (configPopup) {
    return (
      <ConfigurablePopup
        popup={configPopup}
        show={showExitPopup}
        onClose={handleClose}
        onAction={(url) => {
          if (url === "/register") {
            openSignUpModal();
            return true;
          }
          return false;
        }}
      />
    );
  }

  return (
    <Modal
      show={showExitPopup}
      onHide={handleClose}
      centered
      className="exit-popup-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body className="exit-popup-content">
        <ModalCloseButton onClick={handleClose} />

        <img alt="" style={{height: '200px'}} src={require('assets/play365/images/common/exit-image.webp')} />
        <div className="exit-popup-header">
          <h2 className="exit-popup-title">Are you leaving already?</h2>
          <div className="exit-popup-subtitle">
            <p>Don't miss out on your <span className="bonus-highlight">{offerText}</span></p>
            <p>Are you sure you want to leave?</p>
          </div>
        </div>

        <div className="exit-popup-background-elements">
          <div className="coin coin-1">🪙</div>
          <div className="coin coin-2">🪙</div>
          <div className="coin coin-3">🪙</div>
          <div className="card card-1">🃏</div>
          <div className="card card-2">🃏</div>
          <div className="dice dice-1">🎲</div>
        </div>

        <div className="exit-popup-actions">
          <button className="exit-popup-button-primary" onClick={handleClaim}>
            Back to Registration
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ExitIntentPopup;
